import { isTerminal } from "./model.js";
import type { NarrationBeat } from "./model.js";
import type { RunAction, RunState } from "./reducer.js";
import { runNarrationAppended } from "./reducer.js";

/**
 * Validate one raw narration frame, or return null.
 *
 * Deliberately stricter than the typed-update path in one way and looser in
 * another. Stricter: the text becomes a chat message verbatim, so an empty or
 * non-string body is dropped rather than rendered blank. Looser: there is no
 * schema to parse against and no `seq` to fold on, because narration is
 * commentary rather than part of the forecast document.
 */
export function parseNarration(frame: unknown): NarrationBeat | null {
  if (frame === null || typeof frame !== "object") return null;
  const payload = frame as Record<string, unknown>;
  const text = payload.text;
  const ordinal = payload.ordinal;
  if (typeof text !== "string" || text.trim().length === 0) return null;
  if (typeof ordinal !== "number" || !Number.isSafeInteger(ordinal) || ordinal < 0) return null;
  const phase = payload.phase;
  return {
    ordinal,
    text: text.trim(),
    phase: typeof phase === "string" && phase.trim().length > 0 ? phase.trim() : null,
  };
}

/** Parse and dispatch one narration beat while its target run remains live. */
export function applyNarrationFrame(
  dispatch: (action: RunAction) => void,
  getState: () => Pick<RunState, "runsById" | "sharedRunsById">,
  runId: string,
  frame: unknown,
): boolean {
  const state = getState();
  const run = state.runsById[runId] ?? state.sharedRunsById?.[runId];
  if (!run || isTerminal(run.status)) return false;
  const beat = parseNarration(frame);
  if (!beat) return false;
  dispatch(runNarrationAppended(runId, beat));
  return true;
}
